import { Trophy, TrendingUp, TrendingDown, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { SimStock } from "@/hooks/useSimulator";

interface Props {
  data: {
    round: number;
    summary: {
      netWorth: number;
      cash: number;
      portfolioValue: number;
      hw500: number;
      cycle: string;
      sentiment: number;
      bestPerformer: { ticker: string; change: number };
      worstPerformer: { ticker: string; change: number };
      dividendIncome: number;
    };
    news: Array<{ headline: string; sentiment: string }>;
  };
  stocks: SimStock[];
  onContinue: () => void;
}

const RoundSummary = ({ data, stocks, onContinue }: Props) => {
  const { summary: s, news } = data;
  const bestStock = stocks.find((st) => st.ticker === s.bestPerformer.ticker);
  const worstStock = stocks.find((st) => st.ticker === s.worstPerformer.ticker);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="glass-card glow-border max-w-md w-full p-6 space-y-5">
        <div className="text-center">
          <p className="text-accent/60 text-xs font-heading mb-1">ROUND {data.round} COMPLETE</p>
          <h2 className="font-heading font-bold text-2xl text-foreground">Round Summary</h2>
        </div>

        {/* Net Worth */}
        <div className="text-center py-3 border border-border rounded-xl bg-muted/30">
          <p className="text-muted-foreground text-xs">Net Worth</p>
          <p className="font-heading font-bold text-2xl text-accent">${s.netWorth.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
          <div className="flex justify-center gap-4 mt-1 text-xs">
            <span className="text-emerald-400/70">Cash: ${s.cash.toFixed(2)}</span>
            <span className="text-accent/70">Portfolio: ${s.portfolioValue.toFixed(2)}</span>
          </div>
        </div>

        {/* Best / Worst */}
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 rounded-xl border border-emerald-400/20 bg-emerald-400/5">
            <div className="flex items-center gap-1 mb-1">
              <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-emerald-400 text-xs font-heading font-bold">Best</span>
            </div>
            <p className="text-foreground font-heading font-bold text-sm">{s.bestPerformer.ticker}</p>
            <p className="text-emerald-400 text-xs">+{s.bestPerformer.change.toFixed(2)}%</p>
          </div>
          <div className="p-3 rounded-xl border border-red-400/20 bg-red-400/5">
            <div className="flex items-center gap-1 mb-1">
              <TrendingDown className="w-3.5 h-3.5 text-red-400" />
              <span className="text-red-400 text-xs font-heading font-bold">Worst</span>
            </div>
            <p className="text-foreground font-heading font-bold text-sm">{s.worstPerformer.ticker}</p>
            <p className="text-red-400 text-xs">{s.worstPerformer.change.toFixed(2)}%</p>
          </div>
        </div>

        {/* Dividends */}
        {s.dividendIncome > 0 && (
          <div className="flex items-center gap-2 text-amber-400/80 text-xs bg-amber-400/5 border border-amber-400/20 rounded-lg px-3 py-2">
            <DollarSign className="w-3.5 h-3.5" />
            Dividend income: +${s.dividendIncome.toFixed(2)}
          </div>
        )}

        {/* Key news */}
        {news.length > 0 && (
          <div className="space-y-1">
            <p className="text-muted-foreground text-xs font-heading">Key Headlines</p>
            {news.slice(0, 3).map((n, i) => (
              <p key={i} className={`text-xs ${n.sentiment === "positive" ? "text-emerald-400/70" : "text-red-400/70"}`}>
                {n.sentiment === "positive" ? "📈" : "📉"} {n.headline}
              </p>
            ))}
          </div>
        )}

        <Button
          onClick={onContinue}
          className="w-full bg-accent/20 border border-accent/50 text-accent hover:bg-accent/30 font-heading font-bold shadow-[0_0_20px_hsl(var(--accent)/0.2)]"
        >
          Continue Trading →
        </Button>
      </div>
    </div>
  );
};

export default RoundSummary;
